import type {
  HttpCancelTokenSource,
  HttpRequestCancelFilters,
  HttpRequestParams,
  HttpServiceResponse,
  HttpMethodParams,
  HttpCancelTokens,
  HttpResponse,
} from 'http'
import { setHeader } from 'h3'
import omit from 'lodash/omit.js'
import isEqual from 'lodash/isEqual.js'
import { callWithNuxt } from '#app'
import type { FetchResponse } from 'ofetch'
import { parseURL, getQuery } from 'ufo'
import { CacheService } from './Cache.service'
import { CancelError } from '@/plugins/api/CancelError'
import type { DebugRequestStatus, Redirect } from '~/models'
import { WidgetService } from '~/services/Widget.service'

export class HttpService {
  private cacheService!: CacheService
  private readonly requestHeaders: Dictionary
  private $cancelTokensStore: any
  private $locationStore: any
  private $authStore: any
  private $debugStore: any
  private $captchaStore: any
  private readonly apiBaseUrl: string
  private readonly privateApiBaseUrl: string
  private readonly setGlobalError
  private readonly redirect: any
  private readonly context: any
  private readonly widgetService: WidgetService
  private readonly event: any

  constructor(
    context: any,
    requestHeaders: object,
    apiBaseUrl: string,
    privateApiBaseUrl: string,
    redirect: any,
    cancelTokenStore: any,
    locationStore: any,
    authStore: any,
    debugStore: any,
    cacheStore: any,
    captchaStore: any,
    setGlobalError: any,
    widgetService: WidgetService,
    event: any,
  ) {
    this.requestHeaders = requestHeaders || {}
    this.$cancelTokensStore = cancelTokenStore
    this.$locationStore = locationStore
    this.$authStore = authStore
    this.$debugStore = debugStore
    this.$captchaStore = captchaStore
    this.setGlobalError = setGlobalError
    this.cacheService = new CacheService(cacheStore)
    this.apiBaseUrl = apiBaseUrl
    this.privateApiBaseUrl = privateApiBaseUrl
    this.context = context
    this.redirect = redirect
    this.widgetService = widgetService
    this.event = event
  }

  private get baseUrl() {
    return process.server ? this.privateApiBaseUrl : this.apiBaseUrl
  }

  private get cancelTokens(): HttpCancelTokens {
    return this.$cancelTokensStore.cancelTokens
  }

  private get currentLocationDomain(): string {
    return this.$locationStore.currentLocation?.domain ?? ''
  }

  private get currentLocationPrefix(): string {
    return this.$locationStore.locationPrefix ?? ''
  }

  private get openTelemetry() {
    return this.context.$openTelemetry
  }

  public async get<Response>(
    url: string,
    params: HttpMethodParams,
  ): HttpServiceResponse<Response> {
    const cacheKey = params.requestConfig?.cache?.server
      ? !params.requestConfig.cache.server.allCities
        ? this.currentLocationPrefix + url
        : url
      : url

    // Если флага forceRequest нет
    // и данные по совершенному ранее запросу являются актуальными
    // то запрос к бэку выполнять не будем
    if (
      !params.requestConfig?.forceRequest &&
      (await this.cacheService.isCacheValid(cacheKey))
    ) {
      const cachedValue = (await this.cacheService.getCacheItem<Response>({
        key: cacheKey,
        ...params.requestConfig?.cache,
      })) as HttpResponse<Response>

      if (cachedValue) return cachedValue
    }

    params.fetchConfig = {
      ...params.fetchConfig,
      method: 'get',
    }

    const response = await this.sendRequest<Response>(
      url,
      params.fetchConfig,
      params.requestConfig,
    )

    // ошибку отмены запроса
    // не добавляем в кэш
    if (!isEqual(response, CancelError)) {
      await this.cacheService.setCacheItem({
        key: cacheKey,
        ...params.requestConfig?.cache,
        result: response,
      })
    }

    // если ошибка отмены запроса
    // не отправляем запрос и выходим из функции
    if (isEqual(response, CancelError)) {
      return new Promise((resolve) => resolve({} as HttpResponse<Response>))
    }

    return response
  }

  public async post<Response>(
    url: string,
    params: HttpMethodParams,
    attempt = 0,
    maxAttempts = 10,
  ): HttpServiceResponse<Response> {
    if (attempt >= maxAttempts) {
      throw new Error(`Max retry attempts reached for ${url}`)
    }

    params.fetchConfig = {
      ...params.fetchConfig,
      method: 'post',
      headers: {
        ...params.fetchConfig?.headers,
        'X-Captcha-Type': this.$captchaStore.captchaType,
      },
    }

    try {
      const response = await this.sendRequest<Response>(
        url,
        {
          ...params.fetchConfig,
          headers: {
            ...params.fetchConfig?.headers,
            'X-Captcha-Token': localStorage.getItem('captcha_token') || '',
          },
        },
        params.requestConfig,
      )

      if (response.status === 204) {
        await delay(2000)

        return this.post(url, params, attempt + 1, maxAttempts)
      }

      return response
    } catch (e: any) {
      if (e.response.status !== 424) throw e
      const token = await this.$captchaStore.runCaptcha()

      await this.$captchaStore.verifyToken(token)
      const response = await this.sendRequest<Response>(
        url,
        {
          ...params.fetchConfig,
          headers: {
            ...params.fetchConfig?.headers,
            'X-Captcha-Token': localStorage.getItem('captcha_token') || '',
          },
        },
        params.requestConfig,
      )

      return response
    }
  }

  public async put<Response>(
    url: string,
    params: HttpMethodParams,
  ): HttpServiceResponse<Response> {
    params.fetchConfig = {
      ...params.fetchConfig,
      method: 'put',
    }

    return await this.sendRequest<Response>(
      url,
      params.fetchConfig,
      params.requestConfig,
    ).catch((e: any) => {
      throw e
    })
  }

  public async patch<Response>(
    url: string,
    params: HttpMethodParams,
  ): HttpServiceResponse<Response> {
    params.fetchConfig = {
      ...params.fetchConfig,
      method: 'patch',
    }

    return await this.sendRequest<Response>(
      url,
      params.fetchConfig,
      params.requestConfig,
    ).catch((e: any) => {
      throw e
    })
  }

  public async delete<Response>(
    url: string,
    params: HttpMethodParams,
  ): HttpServiceResponse<Response> {
    params.fetchConfig = {
      ...params.fetchConfig,
      method: 'delete',
    }

    return await this.sendRequest<Response>(
      url,
      params.fetchConfig,
      params.requestConfig,
    ).catch((e: any) => {
      throw e
    })
  }

  public async options<Response>(
    url: string,
    params: HttpMethodParams,
  ): HttpServiceResponse<Response> {
    params.fetchConfig = {
      ...params.fetchConfig,
      method: 'options',
    }

    return await this.sendRequest<Response>(
      url,
      params.fetchConfig,
      params.requestConfig,
    ).catch((e: any) => {
      throw e
    })
  }

  public async postFormData<Response>(
    url: string,
    params: HttpMethodParams,
  ): HttpServiceResponse<Response> {
    const formData = new FormData()
    const body = params.fetchConfig!.body as Dictionary

    Object.keys(body).forEach((key) => {
      formData.set(key, body[key])
    })

    params.fetchConfig = {
      ...params.fetchConfig,
      body: formData,
      method: 'post',
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }

    return await this.sendRequest<Response>(
      url,
      params.fetchConfig,
      params.requestConfig,
    ).catch((e: any) => {
      throw e
    })
  }

  setCancelToken(obj: {
    cancelTokenId: string
    token: HttpCancelTokenSource
  }): void {
    this.$cancelTokensStore.setCancelToken(obj)
  }

  deleteCancelToken(urlId: string) {
    this.$cancelTokensStore.deleteCancelToken(urlId)
  }

  cancelRequests(filters: HttpRequestCancelFilters): void {
    try {
      for (const cancelTokenId in this.cancelTokens) {
        const requestOptions: HttpRequestCancelFilters =
          removeTokenPropertyFromFilters(this.cancelTokens, cancelTokenId)

        const needCancel: boolean = checkNeedCancel(requestOptions)

        if (needCancel && process.client) {
          this.cancelTokens[cancelTokenId]?.token?.abort()
          this.deleteCancelToken(cancelTokenId)
        }
      }
    } catch (error: any) {}

    function removeTokenPropertyFromFilters(
      cancelTokens: HttpCancelTokens,
      cancelTokenId: string,
    ): HttpRequestCancelFilters {
      // удаляем свойство token, тк ищем по фильтрам
      return omit(cancelTokens[cancelTokenId], 'token')
    }

    function checkNeedCancel(
      requestOptions: HttpRequestCancelFilters,
    ): boolean {
      // проверяем, что запрос подходит под параметры отмены
      let needCancel = false

      for (const requestOptionKey in requestOptions) {
        const requestOption =
          requestOptions[<keyof HttpRequestCancelFilters>requestOptionKey]

        if (needCancel) break
        needCancel = findFilterInRequestOptions(requestOption, requestOptionKey)
      }

      return needCancel
    }

    function findFilterInRequestOptions(
      requestOption: HttpRequestCancelFilters[keyof HttpRequestCancelFilters],
      requestOptionKey: string,
    ): boolean {
      // ищем совпадения имени и значения фильтра с параметрами запроса

      let filterFounded = false

      for (const filterKey in filters) {
        const filter = filters[<keyof HttpRequestCancelFilters>filterKey]

        if (filter && filterKey === requestOptionKey && requestOption) {
          filterFounded = true
          break
        }
      }

      return filterFounded
    }
  }

  private async sendRequest<Response>(
    url: string,
    fetchConfig: HttpMethodParams['fetchConfig'],
    requestConfig: HttpRequestParams = {},
  ): HttpServiceResponse<Response> {
    const cancelTokenId: string | undefined = this.createCancelToken(
      url,
      fetchConfig,
      requestConfig,
    )

    const { pathname } = parseURL(url)
    const queryFromUrl = getQuery(url)
    const isRefreshRequest = url.includes('/auth/refresh')
    const isAuthRequest = url.includes('/auth/authentication')
    const requestId = url + new Date()

    this.addCustomHeaders(fetchConfig)
    this.addSecureToken(fetchConfig, requestConfig)
    this.createDebugRequestToken(requestId, url, fetchConfig)

    if (requestConfig?.mock) {
      return this.returnMockResponse(requestConfig)
    }

    const finalConfig = this.createFinalRequestConfig(
      url,
      fetchConfig,
      requestConfig,
      queryFromUrl,
    )
    const timeoutId = this.createTimeoutReject(fetchConfig, finalConfig)

    const response = await this.createRequest({
      url: requestConfig?.fullUrl ? url : pathname,
      finalConfig,
      fetchConfig,
      requestConfig,
      isRefreshRequest,
      isAuthRequest,
      cancelTokenId,
      requestId,
      timeoutId,
    })

    return {
      ...response,
      data: response._data,
      status: response.status,
    }
  }

  private async createRequest(config: {
    url: string
    fetchConfig: HttpMethodParams['fetchConfig']
    finalConfig: HttpMethodParams['fetchConfig']
    requestConfig: HttpRequestParams
    cancelTokenId?: string
    requestId: string
    isRefreshRequest: boolean
    isAuthRequest: boolean
    timeoutId?: NodeJS.Timeout
  }) {
    const {
      url,
      finalConfig,
      cancelTokenId,
      requestId,
      requestConfig,
      isRefreshRequest,
      isAuthRequest,
      timeoutId,
      fetchConfig,
    } = config
    const requestSpan = this.openTelemetry.startSpan({
      workName: 'API call',
      spanKind: 2,
      attributes: {
        'http.url': createUrl(
          `${
            requestConfig?.baseUrl ? requestConfig.baseUrl : this.baseUrl
          }${url}`,
          finalConfig?.params || {},
        ).href,
        'http.method': finalConfig?.method?.toUpperCase(),
      },
    })

    return await $fetch
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .raw(url, finalConfig)
      .then((response: any) => {
        if (cancelTokenId && this.cancelTokens[cancelTokenId]) {
          this.deleteCancelToken(cancelTokenId)
        }

        this.updateDebugRequest(requestId, response, 1)

        requestSpan?.end({
          code: 1,
          status: response.status,
        })

        // если нам пришел редирект-кит от бека, отправляем по нему
        if (response._data?.type === 'redirect-page-kit') {
          return this.redirectToExternalPage(response._data!.kit as Redirect)
        }

        // если это page-kit запрос
        // и мы на сервере, то устанавливаем заголовки кэша
        if (url.includes('page-kit') && import.meta.server) {
          const headers = {
            cacheControl: response.headers.get('cache-control'),
            etag: response.headers.get('etag'),
            lastModified: response.headers.get('last-modified'),
            date: response.headers.get('date'),
            vary: response.headers.get('vary'),
          }

          headers.cacheControl &&
            setHeader(this.event, 'Cache-Control', headers.cacheControl)

          headers.etag && setHeader(this.event, 'ETag', headers.etag)

          headers.lastModified &&
            setHeader(this.event, 'Last-Modified', headers.lastModified)

          headers.date && setHeader(this.event, 'Date', headers.date)
          headers.vary && setHeader(this.event, 'Vary', headers.vary)
        }

        return response
      })
      .catch(async (error: any) => {
        requestSpan?.end({
          code: 2,
          status: error.status,
          message: error.message,
        })
        this.updateDebugRequest(requestId, error.response, 2)

        const isUnauthorizedError =
          error?.response?.httpStatus === 401 || error?.response?.status === 401

        if (error.message.includes('abort')) {
          return CancelError
        }

        if (error.response?.status === 404 && requestConfig.needGlobal404) {
          return this.redirectTo404Page()
        }

        // Floodgate
        if (error.response?.status === 428) {
          location.reload()
        }

        // не смогли рефрешнуться
        if (isRefreshRequest && !isUnauthorizedError) {
          return this.$authStore.logout()
        }

        // если выполняется любой запрос вызвавший ошибку авторизации, кроме самого запроса на авторизацию
        if (!isAuthRequest && isUnauthorizedError) {
          return await this.unauthorizedErrorHandler(
            url,
            fetchConfig,
            requestConfig,
            isRefreshRequest,
          )
        }

        if (error.response?.status >= 500) {
          this.setGlobalError(true)
        }

        throw error
      })
      .finally(() => {
        timeoutId && clearTimeout(timeoutId)
      })
  }

  private redirectTo404Page() {
    return callWithNuxt(this.context, this.redirect, [
      `/404`,
      { redirectCode: 404 },
    ])
  }

  private redirectToExternalPage(redirect: Redirect) {
    return callWithNuxt(this.context, this.redirect, [
      redirect.url,
      { external: true, redirectCode: redirect.statusCode || 301 },
    ])
  }

  private async unauthorizedErrorHandler(
    url: string,
    fetchConfig: HttpMethodParams['fetchConfig'],
    requestConfig: HttpRequestParams = {},
    isRefreshRequest: boolean,
  ) {
    if (isRefreshRequest) {
      return this.$authStore.logout()
    } else {
      // пробуем  рефрешнуть токен
      return await this.$authStore.tryRefreshToken().then(async () => {
        // если все ок, переотправляем запрос
        // если не ок, мы поймаем ошибку в sendRequest
        // если она 401, попадем в первый блок
        return await this.sendRequest(url, fetchConfig, requestConfig)
      })
    }
  }

  private createCancelToken(
    url: string,
    fetchConfig: HttpMethodParams['fetchConfig'],
    requestConfig: HttpRequestParams = {},
  ): string | undefined {
    if (process.server) return

    let cancelTokenId = ''

    if (
      (fetchConfig!.method === 'get' || fetchConfig!.method === 'post') &&
      requestConfig.cancellable
    ) {
      cancelTokenId = requestConfig.generalUrlId || url || ''

      this.tryCancelRepeatedRequest(cancelTokenId)

      const token: HttpCancelTokenSource = {
        token: new AbortController(),
        needCancelAtChangeRoute: requestConfig.needCancelAtChangeRoute,
      }

      this.setCancelToken({ cancelTokenId, token })
      fetchConfig!.signal = this.cancelTokens[cancelTokenId]?.token?.signal
    }

    return cancelTokenId
  }

  private tryCancelRepeatedRequest(cancelTokenId: string): void {
    if (typeof this.cancelTokens[cancelTokenId]?.token?.abort !== 'undefined') {
      this.cancelTokens[cancelTokenId]?.token?.abort()
    }
  }

  private addCustomHeaders(fetchConfig: HttpMethodParams['fetchConfig']) {
    const { traceparent, tracestate } =
      this.openTelemetry.getPropagationOutput()

    fetchConfig!.headers = {
      ...this.requestHeaders,
      ...fetchConfig!.headers,
    }

    if (process.server) {
      fetchConfig!.headers = {
        ...fetchConfig!.headers,
        tracestate: (process.server && tracestate) || '',
        traceparent: (process.server && traceparent) || '',
      }
    }
  }

  private addSecureToken(
    fetchConfig: HttpMethodParams['fetchConfig'],
    requestConfig: HttpRequestParams = {},
  ) {
    if (requestConfig.secure && this.$authStore.isAuthorized) {
      fetchConfig!.headers = {
        ...fetchConfig!.headers,
        Authorization: `Bearer ${
          this.$authStore.authToken || this.$authStore.accessTokenCookie
        }`,
      }
    }
  }

  private createFinalRequestConfig(
    url: string,
    fetchConfig: HttpMethodParams['fetchConfig'],
    requestConfig: HttpRequestParams = {},
    queryFromUrl?: any,
  ) {
    const config = this.addWidgetKey(fetchConfig)

    return {
      ...config,
      key: url,
      baseURL: requestConfig?.baseUrl ? requestConfig.baseUrl : this.baseUrl,
      params: {
        ...queryFromUrl,
        ...config!.params,
        domain: requestConfig.fullUrl
          ? undefined
          : config?.params?.domain || this.currentLocationDomain,
        platformState: requestConfig.fullUrl ? undefined : 'website',
      },
      credentials: (requestConfig.omitCredentials
        ? 'omit'
        : 'include') as RequestCredentials,
    }
  }

  private addWidgetKey(fetchConfig: HttpMethodParams['fetchConfig']) {
    if (this.widgetService.enabled) {
      return {
        ...fetchConfig,
        params: {
          ...fetchConfig?.params,
          widgetKey: this.widgetService.getApiKey(),
        },
      }
    }

    return fetchConfig
  }

  private returnMockResponse(requestConfig: HttpRequestParams): {
    headers: any
    data: any
    statusText: string
    status: number
  } {
    return {
      status: 200,
      statusText: 'OK',
      headers: {},
      data: requestConfig!.mock,
    }
  }

  // useFetch не имеет встроенного функционала timeout.
  // поэтому приходится велосипедить
  private createTimeoutReject(
    fetchConfig: HttpMethodParams['fetchConfig'],
    finalConfig: HttpMethodParams['fetchConfig'],
  ) {
    if (fetchConfig?.timeout) {
      const timeoutController = new AbortController()
      const timeoutId = setTimeout(() => {
        timeoutController.abort()
      }, fetchConfig.timeout)

      finalConfig!.signal = timeoutController.signal

      return timeoutId
    }
  }

  private createDebugRequestToken(
    requestId: string,
    url: string,
    finalConfig: HttpMethodParams['fetchConfig'],
  ) {
    this.$debugStore.setDebugRequest(requestId, {
      createdAt: +new Date(),
      url,
      side: process.server ? 'server' : 'client',
      xDebugTokenLink: undefined,
      status: 0,
      request: {
        params: finalConfig?.params,
        body: finalConfig?.body,
      },
      jwtToken: this.$authStore.authToken,
    })
  }

  private updateDebugRequest(
    requestId: string,
    response: FetchResponse<unknown>,
    status: DebugRequestStatus,
  ) {
    if (response) {
      this.$debugStore.updateDebugRequest(
        requestId,
        response.headers.get('x-debug-token-link'),
        status,
        response._data,
      )
    }
  }
}
